import { Button, makeStyles } from '@material-ui/core';
import PriorityHigh from '@material-ui/icons/PriorityHigh';
import { Document, Image as PDFImage, Page, pdf } from '@react-pdf/renderer';
import { LOAD_STATUS } from 'doc-mate-store/lib/constants/load';
import { DOCUMENT_TYPE } from 'doc-mate-store/lib/constants/loadDocument';
import { useStores } from 'doc-mate-store/lib/hooks';
import {
  LoadDocument as LDocument,
  LoadDocumentFile,
} from 'doc-mate-store/lib/models/LoadDocument';
import { OrderLeg } from 'doc-mate-store/lib/models/OrderLeg';
import { saveAs } from 'file-saver';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { PDFDocument } from 'pdf-lib';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import Labeled from '../Labeled';
import LoadDocumentPhotoList from '../LoadDocumentPhotoList';
import { PdfThumbnail } from '../PDFThumbnail';
import DropzoneSection from './DropzoneSection';
import ReorderableFileList from './ReorderableFileList';

type Props = {
  load: OrderLeg;
};

const LoadDocument: React.FC<Props> = ({ load }) => {
  const classes = useStyles();

  const { rootStore } = useStores();
  const { me } = rootStore;
  const user = me ? me.maybeCurrent : undefined;
  const isDispatcherOrBroker = !!(user && (user.isDispatcher || user.isBroker));
  const isDispatcherOrBilling = !!(
    user &&
    (user.isDispatcher || user.isBilling)
  );

  const isMultiOrder = load.isMultiOrder;
  const isInvoiced = load.status === LOAD_STATUS.INVOICED;
  const isEditable = isDispatcherOrBroker && !isInvoiced;

  // Documents
  const billOfLadingDocument = load.getDocument(DOCUMENT_TYPE.BILL_OF_LADING);
  const tripSheetDocument = load.getDocument(DOCUMENT_TYPE.TRIP_SHEET);
  const pickUpDocument = load.getDocument(DOCUMENT_TYPE.PICKUP_LOAD);
  const transitionDocument = load.getDocument(DOCUMENT_TYPE.TRANSITION_PHOTOS);
  const dropOffDocument = load.getDocument(DOCUMENT_TYPE.DROPOFF_LOAD);
  const pickUpDocumentDamages = load.getDocument(DOCUMENT_TYPE.PICKUP_DAMAGES);
  const dropOffDocumentDamages = load.getDocument(
    DOCUMENT_TYPE.DROPOFF_DAMAGES,
  );
  const rateConDocument = load.getDocument(DOCUMENT_TYPE.RATE_CON);
  const additionalDocument = load.getDocument(
    DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS,
  );

  // Photos
  const photosBillOfLading = load.getDocumentForCarousel(
    DOCUMENT_TYPE.BILL_OF_LADING,
  );
  const photosTripSheet = load.getDocumentForCarousel(DOCUMENT_TYPE.TRIP_SHEET);
  const photosTransition = load.getDocumentForCarousel(
    DOCUMENT_TYPE.TRANSITION_PHOTOS,
  );
  const photosPickupLoad = load.getDocumentForCarousel(
    DOCUMENT_TYPE.PICKUP_LOAD,
  );
  const photosPickupDamages = load.getDocumentForCarousel(
    DOCUMENT_TYPE.PICKUP_DAMAGES,
  );
  const photosDropoffLoad = load.getDocumentForCarousel(
    DOCUMENT_TYPE.DROPOFF_LOAD,
  );
  const photosDropoffDamages = load.getDocumentForCarousel(
    DOCUMENT_TYPE.DROPOFF_DAMAGES,
  );
  const photosRateCon = load.getDocumentForCarousel(DOCUMENT_TYPE.RATE_CON);
  const photosAdditionalDocument = load.getDocumentForCarousel(
    DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS,
  );

  // Files
  const filesBillOfLading = load.getDocumentForFiles(
    DOCUMENT_TYPE.BILL_OF_LADING,
  );
  const filesTripSheet = load.getDocumentForFiles(DOCUMENT_TYPE.TRIP_SHEET);
  const filesPickupLoad = load.getDocumentForFiles(DOCUMENT_TYPE.PICKUP_LOAD);
  const filesPickupDamages = load.getDocumentForFiles(
    DOCUMENT_TYPE.PICKUP_DAMAGES,
  );
  const filesDropoffLoad = load.getDocumentForFiles(DOCUMENT_TYPE.DROPOFF_LOAD);
  const filesDropoffDamages = load.getDocumentForFiles(
    DOCUMENT_TYPE.DROPOFF_DAMAGES,
  );
  const filesRateCon = load.getDocumentForFiles(DOCUMENT_TYPE.RATE_CON);
  const filesAdditionalDocument = load.getDocumentForFiles(
    DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS,
  );

  const [uploadingSections, setUploadingSections] = useState<
    Record<string, boolean>
  >({});

  const hasNoFiles =
    photosBillOfLading.length +
      photosTripSheet.length +
      photosPickupLoad.length +
      photosPickupDamages.length +
      photosDropoffLoad.length +
      photosDropoffDamages.length +
      filesBillOfLading.length +
      filesPickupLoad.length +
      filesPickupDamages.length +
      filesDropoffLoad.length +
      filesDropoffDamages.length +
      photosTransition.length +
      photosAdditionalDocument.length +
      filesAdditionalDocument.length +
      (isDispatcherOrBilling
        ? filesRateCon.length + photosRateCon.length
        : 0) ===
    0;

  const isMetalsa =
    !!load.pickup?.maybeCurrent &&
    load.pickup.current.location.maybeCurrent?.name
      .toLowerCase()
      .includes('metalsa');

  const createDocument = useCallback(
    async (documentType: DOCUMENT_TYPE) => {
      const document = new LDocument({
        id: nanoid(),
        leg: load.id,
        createdOn: moment().format(),
        modifiedOn: moment().format(),
        documentType: documentType,
        comment: '',
        photos: [],
      });
      const res = await document.createDocumentWeb(rootStore.token!);
      if (res?.ok) {
        load.documents.add(document);
        return document;
      }
      return undefined;
    },
    [load.documents, load.id, rootStore.token],
  );

  const handleUploadFiles = useCallback(
    async (documentType: DOCUMENT_TYPE, files: File[] | FileList) => {
      if (!files || files.length === 0) return;

      // Prevent re-triggering if already uploading this section
      if (uploadingSections[documentType]) return;

      setUploadingSections(prev => ({ ...prev, [documentType]: true }));

      try {
        let doc = load.getDocument(documentType);
        if (!doc) {
          doc = await createDocument(documentType);
        }
        if (!doc) return;

        const photoFiles: File[] = [];
        const documentFiles: File[] = [];

        Array.from(files).forEach(file => {
          if (file.type === 'image/jpeg' || file.type === 'image/png') {
            photoFiles.push(file);
          } else {
            documentFiles.push(file);
          }
        });

        if (photoFiles.length > 0) {
          for (const photoFile of photoFiles) {
            await doc.addPhotos([photoFile] as any);
          }
        }

        if (documentFiles.length > 0) {
          for (const docFile of documentFiles) {
            await doc.addFiles([docFile] as any);
          }
        }
      } finally {
        setUploadingSections(prev => ({ ...prev, [documentType]: false }));
      }
    },
    [createDocument, load, uploadingSections],
  );

  const handleDownloadPhotos = useCallback(async (photos: any) => {
    const PDFPhotos = () => (
      <Document>
        {photos.map((photo: any) => (
          <Page
            key={photo.id}
            style={{ padding: 10 }}
            orientation={photo.width > photo.height ? 'landscape' : 'portrait'}
          >
            <PDFImage src={photo.src} />
          </Page>
        ))}
      </Document>
    );

    const blob = await pdf(<PDFPhotos />).toBlob();
    return blob;
  }, []);

  const handleDelete = useCallback(
    async (document: LDocument, id: string | number) => {
      await document.deleteDocumentFile(id);
    },
    [],
  );

  const handleDeleteConfirm = useCallback(
    async (document: LDocument, id: string | number) => {
      confirmAlert({
        title: 'Confirm to Delete',
        message: 'Are you sure you want to delete this file?',
        buttons: [
          { label: 'Confirm', onClick: () => handleDelete(document, id) },
          { label: 'Cancel' },
        ],
        overlayClassName: 'react-confirm-alert-overlay-custom',
      });
    },
    [handleDelete],
  );

  const appendSectionToPdf = useCallback(
    async (
      mergedPdf: PDFDocument,
      photos: any[],
      files: LoadDocumentFile[],
    ) => {
      if (photos && photos.length > 0) {
        const photosBlob = await handleDownloadPhotos(photos);
        if (photosBlob) {
          const photosArrayBuffer = await photosBlob.arrayBuffer();
          const photosPdfDoc = await PDFDocument.load(photosArrayBuffer);
          const copiedPages = await mergedPdf.copyPages(
            photosPdfDoc,
            photosPdfDoc.getPageIndices(),
          );
          copiedPages.forEach(page => mergedPdf.addPage(page));
        }
      }

      if (files && files.length > 0) {
        for (const file of files) {
          try {
            const response = await fetch(file.file);
            const arrayBuffer = await response.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const copiedPages = await mergedPdf.copyPages(
              pdfDoc,
              pdfDoc.getPageIndices(),
            );
            copiedPages.forEach(page => mergedPdf.addPage(page));
          } catch (error) {
            console.error(`Failed to merge file ${file.name}:`, error);
          }
        }
      }
    },
    [handleDownloadPhotos],
  );

  const handleDownloadSectionPdf = useCallback(
    async (sectionName: string, photos: any[], files: LoadDocumentFile[]) => {
      const mergedPdf = await PDFDocument.create();
      const pdfFileName = `${load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber} - ${sectionName}.pdf`;

      await appendSectionToPdf(mergedPdf, photos, files);

      if (mergedPdf.getPageCount() === 0) {
        alert('No documents or photos available to export.');
        return;
      }

      const mergedPdfBytes = await mergedPdf.save();
      const finalBlob = new Blob([mergedPdfBytes], {
        type: 'application/pdf',
      });
      saveAs(finalBlob, pdfFileName);
    },
    [
      appendSectionToPdf,
      load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber,
    ],
  );

  const handleDownloadCoreDocsPdf = useCallback(async () => {
    const mergedPdf = await PDFDocument.create();
    const pdfFileName = `${load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber}.pdf`;
    if (isDispatcherOrBilling) {
      await appendSectionToPdf(mergedPdf, photosRateCon, filesRateCon);
    }
    await appendSectionToPdf(mergedPdf, photosBillOfLading, filesBillOfLading);
    await appendSectionToPdf(
      mergedPdf,
      photosAdditionalDocument,
      filesAdditionalDocument,
    );

    if (mergedPdf.getPageCount() === 0) {
      alert('No documents or photos available to export.');
      return;
    }

    const mergedPdfBytes = await mergedPdf.save();
    const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    saveAs(finalBlob, pdfFileName);
  }, [
    appendSectionToPdf,
    filesAdditionalDocument,
    filesBillOfLading,
    filesRateCon,
    isDispatcherOrBilling,
    load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber,
    photosAdditionalDocument,
    photosBillOfLading,
    photosRateCon,
  ]);

  const handleDownloadFullLoadPdf = useCallback(async () => {
    const mergedPdf = await PDFDocument.create();
    const pdfFileName = `${load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber}.pdf`;

    if (isDispatcherOrBilling) {
      await appendSectionToPdf(mergedPdf, photosRateCon, filesRateCon);
    }
    await appendSectionToPdf(mergedPdf, photosBillOfLading, filesBillOfLading);
    await appendSectionToPdf(
      mergedPdf,
      photosAdditionalDocument,
      filesAdditionalDocument,
    );
    await appendSectionToPdf(mergedPdf, photosTripSheet, filesTripSheet);
    await appendSectionToPdf(mergedPdf, photosPickupLoad, filesPickupLoad);
    await appendSectionToPdf(
      mergedPdf,
      photosPickupDamages,
      filesPickupDamages,
    );
    await appendSectionToPdf(mergedPdf, photosTransition, []);
    await appendSectionToPdf(mergedPdf, photosDropoffLoad, filesDropoffLoad);
    await appendSectionToPdf(
      mergedPdf,
      photosDropoffDamages,
      filesDropoffDamages,
    );

    if (mergedPdf.getPageCount() === 0) {
      alert('No documents or photos available to export.');
      return;
    }

    const mergedPdfBytes = await mergedPdf.save();
    const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    saveAs(finalBlob, pdfFileName);
  }, [
    appendSectionToPdf,
    filesAdditionalDocument,
    filesBillOfLading,
    filesDropoffDamages,
    filesDropoffLoad,
    filesPickupDamages,
    filesPickupLoad,
    filesRateCon,
    filesTripSheet,
    isDispatcherOrBilling,
    load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber,
    photosAdditionalDocument,
    photosBillOfLading,
    photosDropoffDamages,
    photosDropoffLoad,
    photosPickupDamages,
    photosPickupLoad,
    photosRateCon,
    photosTransition,
    photosTripSheet,
  ]);

  const handleDownloadAllStopsPaperworkPdf = useCallback(async () => {
    const mergedPdf = await PDFDocument.create();
    const pdfFileName = `${load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber}.pdf`;

    if (isDispatcherOrBilling) {
      await appendSectionToPdf(mergedPdf, photosRateCon, filesRateCon);
    }
    await appendSectionToPdf(mergedPdf, photosBillOfLading, filesBillOfLading);
    await appendSectionToPdf(
      mergedPdf,
      photosAdditionalDocument,
      filesAdditionalDocument,
    );

    if (mergedPdf.getPageCount() === 0) {
      alert('No paperwork or documents available to export yet.');
      return;
    }

    const mergedPdfBytes = await mergedPdf.save();
    const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    saveAs(finalBlob, pdfFileName);
  }, [
    appendSectionToPdf,
    filesAdditionalDocument,
    filesBillOfLading,
    filesRateCon,
    isDispatcherOrBilling,
    load.order.maybeCurrent?.manifest.maybeCurrent?.as400ManifestNumber,
    photosAdditionalDocument,
    photosBillOfLading,
    photosRateCon,
  ]);

  if (hasNoFiles && !isDispatcherOrBroker) {
    return (
      <div className={classes.loadDetailItem}>
        <Labeled label="Documents &amp; Photos">To be uploaded</Labeled>
      </div>
    );
  }

  return (
    <div className={classes.loadDetailItem}>
      <Labeled label="Documents &amp; Photos">
        <div
          style={{
            flexDirection: 'column',
            display: 'flex',
            alignItems: 'start',
          }}
        >
          {isMultiOrder && isDispatcherOrBilling && (
            <Button onClick={handleDownloadAllStopsPaperworkPdf}>
              Download All Paperworks for All Stops
            </Button>
          )}

          {isDispatcherOrBilling && (
            <Button onClick={handleDownloadCoreDocsPdf}>
              Download All Paperwork
            </Button>
          )}

          {isDispatcherOrBilling && (
            <Button onClick={handleDownloadFullLoadPdf}>Download All</Button>
          )}
        </div>

        {isDispatcherOrBilling && load.status === LOAD_STATUS.BILLING_ISSUE && (
          <div className={classes.billingIssueNotice}>
            <PriorityHigh className={classes.priorityHigh} />
            <div>
              <span>Billing noted billing issue(s):</span>
              {load.billingIssueDescription
                .split(/,\s*|\n/)
                .filter(Boolean)
                .map((issue, index) => (
                  <div key={index} className={classes.billingIssueItem}>
                    • &quot;{issue.trim()}&quot;
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* 1. RATE CON */}
        {(photosRateCon.length + filesRateCon.length > 0 ||
          isDispatcherOrBroker) && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.RATE_CON}
            isDisabled={!isEditable}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.RATE_CON]}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Rate Con"
                className={classes.photoList}
                showDownloadIcon={
                  photosRateCon.length + filesRateCon.length > 0
                }
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Rate Con',
                    photosRateCon,
                    filesRateCon,
                  )
                }
              >
                {(photosRateCon.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>DOCUMENTS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosRateCon}
                        caption="Rate Con Photos"
                        onAdd={openFileDialog}
                        document={rateConDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}
                {filesRateCon.length > 0 && (
                  <ReorderableFileList
                    files={filesRateCon}
                    document={rateConDocument}
                    isEditable={isEditable}
                    onDeleteConfirm={handleDeleteConfirm}
                  />
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 2. BILL OF LADING */}
        {(photosBillOfLading.length + filesBillOfLading.length > 0 ||
          isDispatcherOrBroker) && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.BILL_OF_LADING}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.BILL_OF_LADING]}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Bill of Lading"
                className={classes.photoList}
                showDownloadIcon={
                  photosBillOfLading.length + filesBillOfLading.length > 0
                }
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Bill Of Lading',
                    photosBillOfLading,
                    filesBillOfLading,
                  )
                }
              >
                {(photosBillOfLading.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>DOCUMENTS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosBillOfLading}
                        caption="Bill of Lading Photos"
                        onAdd={openFileDialog}
                        document={billOfLadingDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}
                {filesBillOfLading.length > 0 && (
                  <ReorderableFileList
                    files={filesBillOfLading}
                    document={billOfLadingDocument}
                    isEditable={isEditable}
                    onDeleteConfirm={handleDeleteConfirm}
                  />
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 3. ADDITIONAL DOCUMENTS */}
        {isDispatcherOrBroker &&
          (photosAdditionalDocument.length + filesAdditionalDocument.length >
            0 ||
            isDispatcherOrBroker) && (
            <DropzoneSection
              documentType={DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS}
              isUploading={
                !!uploadingSections[DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS]
              }
              isDisabled={!isEditable}
              onUpload={handleUploadFiles}
            >
              {openFileDialog => (
                <Labeled
                  inner
                  label="Additional Documents"
                  className={classes.photoList}
                  showDownloadIcon={
                    photosAdditionalDocument.length +
                      filesAdditionalDocument.length >
                    0
                  }
                  textClassName={classes.sectionText}
                  onClick={() =>
                    handleDownloadSectionPdf(
                      'Additional Documents',
                      photosAdditionalDocument,
                      filesAdditionalDocument,
                    )
                  }
                >
                  {(photosAdditionalDocument.length > 0 ||
                    isDispatcherOrBroker) && (
                    <>
                      <span className={classes.documentText}>DOCUMENTS:</span>
                      <div className={classes.photoListWrapper}>
                        <LoadDocumentPhotoList
                          photos={photosAdditionalDocument}
                          caption="Additional Documents Photos"
                          onAdd={openFileDialog}
                          document={additionalDocument}
                          canUpdate={isEditable}
                        />
                      </div>
                    </>
                  )}
                  {filesAdditionalDocument.length > 0 && (
                    <ReorderableFileList
                      files={filesAdditionalDocument}
                      document={additionalDocument}
                      isEditable={isEditable}
                      onDeleteConfirm={handleDeleteConfirm}
                    />
                  )}
                </Labeled>
              )}
            </DropzoneSection>
          )}

        {/* 4. TRIP SHEET */}
        {isDispatcherOrBilling && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.TRIP_SHEET}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.TRIP_SHEET]}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Trip Sheet"
                className={classes.photoList}
                showDownloadIcon={
                  photosTripSheet.length + filesTripSheet.length > 0
                }
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Trip Sheet',
                    photosTripSheet,
                    filesTripSheet,
                  )
                }
              >
                {(photosTripSheet.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>DOCUMENTS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosTripSheet}
                        caption="Trip Sheet Photos"
                        onAdd={openFileDialog}
                        document={tripSheetDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}
                {filesTripSheet.length > 0 && (
                  <ReorderableFileList
                    files={filesTripSheet}
                    document={tripSheetDocument}
                    isEditable={isEditable}
                    onDeleteConfirm={handleDeleteConfirm}
                  />
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 5. PICKUP LOAD */}
        {(photosPickupLoad.length + filesPickupLoad.length > 0 ||
          (isDispatcherOrBroker &&
            (Number(load.linkedLegCount) < 2 || isMetalsa))) && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.PICKUP_LOAD}
            isDisabled={!isEditable}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.PICKUP_LOAD]}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Load at Pick-up"
                className={classes.photoList}
                showDownloadIcon={photosPickupLoad.length > 0}
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Load at Pick-up',
                    photosPickupLoad,
                    filesPickupLoad,
                  )
                }
              >
                {(photosPickupLoad.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>PHOTOS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosPickupLoad}
                        caption="Load at Pick-up"
                        onAdd={openFileDialog}
                        document={pickUpDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}

                {filesPickupLoad.length > 0 && (
                  <div className={classes.fileListWrapper}>
                    {filesPickupLoad.map(file => (
                      <div className={classes.fileWrapper} key={file.id}>
                        <a
                          href={file.file}
                          className={classes.file}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          <PdfThumbnail
                            pdfUrl={file.file}
                            width={80}
                            onClick={() => {}}
                          />
                          <span className={classes.fileName}>{file.name}</span>
                        </a>
                        {isEditable && (
                          <Button
                            className={classes.button}
                            onClick={() =>
                              handleDeleteConfirm(pickUpDocument!, file.id)
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!(
                  load.pickupHasNoDamages || photosPickupDamages.length === 0
                ) && (
                  <div className={classes.damages}>
                    <PriorityHigh className={classes.priorityHigh} />
                    Driver noted damages, shortages or returns at pick-up
                  </div>
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 6. PICKUP DAMAGES */}
        {photosPickupDamages.length + filesPickupDamages.length > 0 && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.PICKUP_DAMAGES}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.PICKUP_DAMAGES]}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Damages, Shortages &amp; Returns at Pick-up"
                className={classes.photoList}
                showDownloadIcon={photosPickupDamages.length > 0}
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Pick-up Damages',
                    photosPickupDamages,
                    filesPickupDamages,
                  )
                }
              >
                {photosPickupDamages.length > 0 && (
                  <>
                    <span className={classes.documentText}>PHOTOS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosPickupDamages}
                        caption="Damages, Shortages &amp; Returns at Pick-up"
                        onAdd={openFileDialog}
                        document={pickUpDocumentDamages}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}

                {filesPickupDamages.length > 0 && (
                  <div className={classes.fileListWrapper}>
                    {filesPickupDamages.map(file => (
                      <div className={classes.fileWrapper} key={file.id}>
                        <a
                          href={file.file}
                          className={classes.file}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          <PdfThumbnail
                            pdfUrl={file.file}
                            width={80}
                            onClick={() => {}}
                          />
                          <span className={classes.fileName}>{file.name}</span>
                        </a>
                        {isEditable && (
                          <Button
                            className={classes.button}
                            onClick={() =>
                              handleDeleteConfirm(
                                pickUpDocumentDamages!,
                                file.id,
                              )
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* RESECURED / TRANSITION PHOTOS */}
        {(photosTransition.length > 0 ||
          (isDispatcherOrBroker &&
            Number(load.linkedLegCount) > 1 &&
            !isMetalsa)) && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.TRANSITION_PHOTOS}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.TRANSITION_PHOTOS]}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Resecured Photos"
                className={classes.photoList}
                showDownloadIcon={photosTransition.length > 0}
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Resecured Photos',
                    photosTransition,
                    [],
                  )
                }
              >
                {(photosTransition.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>PHOTOS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosTransition}
                        caption="Resecured Photos"
                        onAdd={openFileDialog}
                        document={transitionDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 7. DROPOFF LOAD */}
        {(photosDropoffLoad.length + filesDropoffLoad.length > 0 ||
          isDispatcherOrBroker) && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.DROPOFF_LOAD}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.DROPOFF_LOAD]}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Load at Drop-off"
                className={classes.photoList}
                showDownloadIcon={photosDropoffLoad.length > 0}
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Load at Drop-off',
                    photosDropoffLoad,
                    filesDropoffLoad,
                  )
                }
              >
                {(photosDropoffLoad.length > 0 || isDispatcherOrBroker) && (
                  <>
                    <span className={classes.documentText}>PHOTOS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosDropoffLoad}
                        caption="Load at Drop-off"
                        onAdd={openFileDialog}
                        document={dropOffDocument}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}

                {filesDropoffLoad.length > 0 && (
                  <div className={classes.fileListWrapper}>
                    {filesDropoffLoad.map(file => (
                      <div className={classes.fileWrapper} key={file.id}>
                        <a
                          href={file.file}
                          className={classes.file}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          <PdfThumbnail
                            pdfUrl={file.file}
                            width={80}
                            onClick={() => {}}
                          />
                          <span className={classes.fileName}>{file.name}</span>
                        </a>
                        {isEditable && (
                          <Button
                            className={classes.button}
                            onClick={() =>
                              handleDeleteConfirm(dropOffDocument!, file.id)
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!(
                  load.dropoffHasNoDamages || photosDropoffDamages.length === 0
                ) && (
                  <div className={classes.damages}>
                    <PriorityHigh className={classes.priorityHigh} />
                    Driver noted damages, shortages or returns at drop-off
                  </div>
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}

        {/* 8. DROPOFF DAMAGES */}
        {photosDropoffDamages.length + filesDropoffDamages.length > 0 && (
          <DropzoneSection
            documentType={DOCUMENT_TYPE.DROPOFF_DAMAGES}
            isUploading={!!uploadingSections[DOCUMENT_TYPE.DROPOFF_DAMAGES]}
            isDisabled={!isEditable}
            onUpload={handleUploadFiles}
          >
            {openFileDialog => (
              <Labeled
                inner
                label="Damages, Shortages &amp; Returns at Drop-off"
                className={classes.photoList}
                showDownloadIcon={photosDropoffDamages.length > 0}
                textClassName={classes.sectionText}
                onClick={() =>
                  handleDownloadSectionPdf(
                    'Drop-off Damages',
                    photosDropoffDamages,
                    filesDropoffDamages,
                  )
                }
              >
                {photosDropoffDamages.length > 0 && (
                  <>
                    <span className={classes.documentText}>PHOTOS:</span>
                    <div className={classes.photoListWrapper}>
                      <LoadDocumentPhotoList
                        photos={photosDropoffDamages}
                        caption="Damages, Shortages &amp; Returns at Drop-off"
                        onAdd={openFileDialog}
                        document={dropOffDocumentDamages}
                        canUpdate={isEditable}
                      />
                    </div>
                  </>
                )}

                {filesDropoffDamages.length > 0 && (
                  <div className={classes.fileListWrapper}>
                    {filesDropoffDamages.map(file => (
                      <div className={classes.fileWrapper} key={file.id}>
                        <a
                          href={file.file}
                          className={classes.file}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          <PdfThumbnail
                            pdfUrl={file.file}
                            width={80}
                            onClick={() => {}}
                          />
                          <span className={classes.fileName}>{file.name}</span>
                        </a>
                        {isEditable && (
                          <Button
                            className={classes.button}
                            onClick={() =>
                              handleDeleteConfirm(
                                dropOffDocumentDamages!,
                                file.id,
                              )
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Labeled>
            )}
          </DropzoneSection>
        )}
      </Labeled>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  dropzoneContainer: {
    position: 'relative',
    borderRadius: 4,
    transition: 'all 0.2s ease-in-out',
    marginBottom: theme.spacing(1),
  },
  dropzoneActive: {
    outline: '2px dashed #0d374b',
    backgroundColor: 'rgba(13, 55, 75, 0.04)',
  },
  dropzoneOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: 14,
    color: '#0d374b',
    zIndex: 10,
    borderRadius: 4,
    border: '2px dashed #0d374b',
    pointerEvents: 'none',
  },
  loadDetailItem: {
    alignItems: 'center',
    display: 'flex',
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    fontWeight: 500,
  },
  noDamages: {
    clear: 'both',
    fontSize: theme.typography.body2.fontSize,
    marginLeft: -theme.spacing(1.5),
  },
  documentText: {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 2,
    textTransform: 'uppercase',
    marginLeft: 1,
  },
  fileListWrapper: {
    marginTop: 4,
  },
  photoListWrapper: {
    marginLeft: 8,
    marginTop: 4,
  },
  fileWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  file: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: 8,
    marginTop: 4,
    color: '#0d374b',
    textDecoration: 'none',
  },
  fileName: {
    fontSize: 11,
    textOverflow: 'hidden',
    marginLeft: 2,
    marginRight: 2,
  },
  pdfIcon: {
    width: 40,
    height: 40,
  },
  damages: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.palette.error.main,
    marginLeft: -theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  priorityHigh: {
    height: 20,
  },
  photoList: {
    marginBottom: theme.spacing(1),
    clear: 'both',
  },
  button: {
    padding: 0,
    fontWeight: 700,
    marginLeft: theme.spacing(0.5),
    background: theme.palette.error.main,
    color: 'white',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  sectionText: {
    fontSize: 14,
    lineHeight: 2,
  },
  billingIssueNotice: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  billingIssueItem: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

export default observer(LoadDocument);
